import { useTranslation } from '../../../i18n/useTranslation';
import {
  AUTH_TYPE_LABELS,
  TTS_SERVICE_LEVEL_LABELS,
  type BooksellerFilterState,
  type BooksellerManageDto,
} from '../../../types/manageBooksellers';

interface Props {
  rows: BooksellerManageDto[];
  totalCount: number;
  filters: BooksellerFilterState;
  onChange: (patch: Partial<BooksellerFilterState>) => void;
  onEdit: (id: string) => void;
  onDelete: (row: BooksellerManageDto) => void;
}

type SortField = NonNullable<BooksellerFilterState['sort']>;

export default function BooksellerListTable({
  rows, totalCount, filters, onChange, onEdit, onDelete,
}: Props) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));

  const sortable = (field: SortField, label: string) => {
    const active = filters.sort === field;
    const nextDir: 'asc' | 'desc' = active && filters.dir === 'asc' ? 'desc' : 'asc';
    return (
      <button
        type="button"
        onClick={() => onChange({ sort: field, dir: nextDir, page: 1 })}
        className={`text-xs uppercase tracking-wide ${active ? 'text-primary-600' : 'text-neutral-600'}`}
      >
        {label} {active ? (filters.dir === 'asc' ? '▲' : '▼') : ''}
      </button>
    );
  };

  return (
    <div>
      <div className="overflow-x-auto border border-neutral-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="p-2 text-left">{sortable('name', t('App.Admin.Booksellers.BooksellerListTable.ColName'))}</th>
              <th className="p-2 text-left">{sortable('slug', t('App.Admin.Booksellers.BooksellerListTable.ColSlug'))}</th>
              <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Booksellers.BooksellerListTable.ColDisplayName')}</th>
              <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Booksellers.BooksellerListTable.ColActive')}</th>
              <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Booksellers.BooksellerListTable.ColAuthType')}</th>
              <th className="p-2 text-right">{sortable('priority', t('App.Admin.Booksellers.BooksellerListTable.ColPriority'))}</th>
              <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Booksellers.BooksellerListTable.ColTtsLevel')}</th>
              <th className="p-2 text-left">{sortable('creationTime', t('App.Admin.Booksellers.BooksellerListTable.ColCreated'))}</th>
              <th className="p-2 text-right text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Booksellers.BooksellerListTable.ColActions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                <td className="p-2">
                  <button type="button" onClick={() => onEdit(r.id)} className="text-primary-600 hover:underline">
                    {r.name}
                  </button>
                </td>
                <td className="p-2 font-mono text-xs">{r.slug}</td>
                <td className="p-2">{r.displayName}</td>
                <td className="p-2">{r.isActive ? t('App.Admin.Booksellers.BooksellerListTable.ActiveYes') : t('App.Admin.Booksellers.BooksellerListTable.ActiveNo')}</td>
                <td className="p-2">{AUTH_TYPE_LABELS[r.authType] ?? r.authType}</td>
                <td className="p-2 text-right tabular-nums">{r.priority}</td>
                <td className="p-2">{TTS_SERVICE_LEVEL_LABELS[r.ttsServiceLevel] ?? r.ttsServiceLevel}</td>
                <td className="p-2">{new Date(r.creationTime).toLocaleDateString()}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onEdit(r.id)}
                    className="text-primary-600 hover:underline mr-3"
                  >
                    {t('App.Admin.Booksellers.BooksellerListTable.EditAction')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(r)}
                    className="text-red-600 hover:underline"
                  >
                    {t('App.Admin.Booksellers.BooksellerListTable.DeleteAction')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="p-6 text-center text-sm text-neutral-500">{t('App.Admin.Booksellers.BooksellerListTable.NoResults')}</div>
        )}
      </div>

      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="flex items-center gap-3 text-neutral-600">
          <span>
            {t('App.Admin.Booksellers.BooksellerListTable.PageSummary', { page: filters.page, totalPages, totalCount, count: totalCount })}
          </span>
          <label className="flex items-center gap-1">
            <span className="text-xs">{t('App.Admin.Booksellers.BooksellerListTable.RowsLabel')}</span>
            <select
              value={filters.pageSize}
              onChange={(e) => onChange({ pageSize: Number(e.target.value), page: 1 })}
              className="border border-neutral-300 rounded px-1 py-0.5 text-sm"
              aria-label={t('App.Admin.Booksellers.BooksellerListTable.RowsAriaLabel')}
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={filters.page <= 1}
            onClick={() => onChange({ page: filters.page - 1 })}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Booksellers.BooksellerListTable.PrevButton')}
          </button>
          <button
            type="button"
            disabled={filters.page >= totalPages}
            onClick={() => onChange({ page: filters.page + 1 })}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Booksellers.BooksellerListTable.NextButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
